import { useEffect, useState } from 'react';

import { BREAKPOINTS, Breakpoint } from '@/styles/themes/default';
import { isServer } from '@/utils/isServer';

function buildMediaQueryString(breakpoint: Breakpoint) {
  const { min, max } = BREAKPOINTS[breakpoint];

  if (breakpoint === Breakpoint.Desktop) {
    return `(min-width: ${min}px)`;
  }

  return `(min-width: ${min}px) and (max-width: ${max}px)`;
}

function getInitialValue(): Breakpoint {
  if (isServer()) {
    return Breakpoint.Unknown;
  }

  const breakpoint = Object.keys(BREAKPOINTS).find((key) => {
    const queryString = buildMediaQueryString(key as Breakpoint);

    return window.matchMedia(queryString).matches;
  });

  return breakpoint as Breakpoint;
}

function useQueryChange(breakpoint: Breakpoint, setCurrentBreakpoint: (breakpoint: Breakpoint) => void) {
  useEffect(() => {
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setCurrentBreakpoint(breakpoint);
      }
    };

    const mediaQueryList = window.matchMedia(buildMediaQueryString(breakpoint));

    mediaQueryList.addListener(handleChange);
    return () => {
      mediaQueryList.removeListener(handleChange);
    };
  }, [breakpoint, setCurrentBreakpoint]);
}

export interface IUseBreakpoints {
  isUnknown: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  value: Breakpoint;
}

/**
 * @deprecated
 * Returns the result that can be used to dynamically render components
 * based on current screen width.
 *
 * IMPORTANT: While screen is being rendered, the width can be 0 for few
 * milliseconds so in that case, all 3 values, isMobile, isTablet and isDesktop
 * will be false! Take care not to render something accidentally if you are using
 * negated values! You can detect this edge case because Breakpoint value will be Breakpoint.Unknown.
 */
function useBreakpoints(): IUseBreakpoints {
  const [currentBreakpoint, setCurrentBreakpoint] = useState(getInitialValue());

  useQueryChange(Breakpoint.Unknown, setCurrentBreakpoint);
  useQueryChange(Breakpoint.Mobile, setCurrentBreakpoint);
  useQueryChange(Breakpoint.Tablet, setCurrentBreakpoint);
  useQueryChange(Breakpoint.Desktop, setCurrentBreakpoint);

  return {
    value: currentBreakpoint as Breakpoint,
    isUnknown: currentBreakpoint === Breakpoint.Unknown,
    isMobile: currentBreakpoint === Breakpoint.Mobile,
    isTablet: currentBreakpoint === Breakpoint.Tablet,
    isDesktop: currentBreakpoint === Breakpoint.Desktop,
  };
}

export default useBreakpoints;
